import { NewCardPicker, CardOption, CardGroup } from "./NewCardPicker";
import { useDeepLinks } from "src/hooks/useDeepLinks";
import {
  DeepLinkType,
  BoardCardData,
  ExploreCard,
} from "pages/api/deep-links/types";
import { ContentViews } from "pages/[orgId]/explore/lib/useContentView";
import { VizIcon } from "../VizIcon";
import { useToasterContext, ToasterMessageType } from "src/hooks/useToaster";
import { unserializeParams } from "src/search/lib/params-state-reducer";
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { QuestionEditor } from "pages/[orgId]/dashboards/QuestionEditor";
import { useQuestionCard } from "pages/[orgId]/dashboards/QuestionViewer";
import { v4 as uuidv4 } from "uuid";
import { Button, Card, Space } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { Flex } from "../Flex";
import { useApplicationContext } from "src/hooks/useApplication";

// Separate component for the editor to properly handle hooks
function EditorComponent({
  cardId,
  card,
  existingData,
  onUpdate,
  onClose,
}: {
  cardId: string;
  card: ExploreCard;
  existingData: Record<string, BoardCardData>;
  onUpdate: ((data: Record<string, BoardCardData>) => void) | undefined;
  onClose: () => void;
}) {
  const { explore } = useQuestionCard({
    json: { data: { [cardId]: card } },
    cardId,
    searchState: {},
  });

  return (
    <QuestionEditor
      card={card}
      updateCard={(cardData) => {
        if (onUpdate) {
          onUpdate({
            ...existingData,
            [cardId]: cardData,
          });
        }
        onClose();
      }}
      onClose={onClose}
    />
  );
}

interface DataSourcePickerProps {
  onSelect: (source: { id: string; state: any }) => void;
  renderTrigger?: (onClick: () => void) => React.ReactNode;
  existingData?: Record<string, BoardCardData>;
  onUpdate?: (data: Record<string, BoardCardData>) => void;
  onRemove?: (cardId: string) => void;
}

export const DataSourcePicker: React.FC<DataSourcePickerProps> = ({
  onSelect,
  renderTrigger,
  existingData,
  onUpdate,
  onRemove,
}) => {
  const { links } = useDeepLinks(DeepLinkType.EXPLORE);
  const toaster = useToasterContext();
  const [editingCard, setEditingCard] = useState<string | null>(null);
  const { org_id: orgId } = useApplicationContext();

  const cardGroups: CardGroup[] = [
    {
      label: "Saved Queries",
      options: (links.data || []).map((link) => {
        let vizValue = ContentViews.default;
        let state = {
          params: {},
          viz: { value: ContentViews.default, config: {} },
        };

        try {
          const data = JSON.parse(link.state);
          const params = unserializeParams(data.serialized);
          const viz = (data.viz || []).at(0);
          if (viz?.value) vizValue = viz.value;
          state = { params, viz };
        } catch {}

        return {
          key: "key" + link.id,
          name: link.name.toLowerCase(),
          icon: <VizIcon value={vizValue} size={15} />,
          label: link.name,
          value: link.id,
          disabled: false,
          state,
          tags: (link.group || "").split(",").filter(Boolean),
        };
      }),
    },
  ];

  const handleCardSelect = (card: CardOption) => {
    const cardId = uuidv4();
    const cardData: BoardCardData = {
      type: DeepLinkType.EXPLORE,
      state: card.state,
      style: {},
      title: card.name,
      showTitle: true,
      centerTitle: false,
      transparent: false,
    };

    if (existingData && onUpdate) {
      onUpdate({
        ...existingData,
        [cardId]: cardData,
      });
    } else {
      onSelect({
        id: String(card.value),
        state: card.state,
      });
    }

    toaster.addMessage(ToasterMessageType.SUCCESS, {
      message: "Added data source",
    });
  };

  const handleDelete = (cardId: string) => {
    if (existingData && onUpdate) {
      const newData = { ...existingData };
      delete newData[cardId];
      
      // Call onRemove first if it exists
      if (onRemove) {
        onRemove(cardId);
      }
      
      // Then update the data
      onUpdate(newData);
      
      toaster.addMessage(ToasterMessageType.SUCCESS, {
        message: "Removed data source",
      });
    }
  };

  // Add cleanup effect
  useEffect(() => {
    return () => {
      setEditingCard(null);
    };
  }, []);

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      {/* List of existing queries */}
      {existingData && (
        <div style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: "100%" }} size="small">
            {Object.entries(existingData).map(([cardId, card]) => (
              <Card key={cardId} size="small" style={{ width: "100%" }}>
                <Flex spaceBetween centeredY>
                  <div>
                    {card.type === DeepLinkType.EXPLORE && (
                      <>
                        <VizIcon
                          value={(card as ExploreCard).state.viz?.value}
                          size={15}
                          style={{ marginRight: 8, display: "inline-block" }}
                        />
                        {(card as ExploreCard).title}
                      </>
                    )}
                  </div>
                  <Space>
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => setEditingCard(cardId)}
                    />
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={() => handleDelete(cardId)}
                    />
                  </Space>
                </Flex>
              </Card>
            ))}
          </Space>
        </div>
      )}

      {/* Add new query button/picker */}
      <NewCardPicker
        cardGroups={cardGroups}
        onCardSelect={handleCardSelect}
        modalTitle="Add Data Source"
        showBlankCard={true}
        showEntityFilter
        showVisualizationFilter
        showTagsFilter
        renderTrigger={renderTrigger}
      />

      {/* Edit modal with proper hook handling */}
      {editingCard &&
        existingData &&
        createPortal(
          <EditorComponent
            cardId={editingCard}
            card={existingData[editingCard] as ExploreCard}
            existingData={existingData}
            onUpdate={onUpdate}
            onClose={() => setEditingCard(null)}
          />,
          document.body
        )}
    </Space>
  );
};
